import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Heart, MessageCircle, Share2, Music, Clapperboard, ChevronUp, ChevronDown, Eye, AlertTriangle, Smile, X } from 'lucide-react';
import clsx from 'clsx';
import { storiesApi, ForYouItem } from '@/api/stories';
import { SwipaSend } from '@/icons/SwipaSend';
import { apiClient } from '@/api/client';
import Avatar from '@/components/ui/Avatar';
import FollowButton from '@/components/ui/FollowButton';
import { useAuthStore } from '@/store/authStore';
import SoundPage from './SoundPage';
import StoryCreateModal from './StoryCreateModal';

/**
 * Вертикальная лента "For You" в стиле TikTok: одна сториз во весь экран,
 * свайп вверх → следующая. Счёт просмотра делается при входе карточки в viewport.
 */
export default function ForYouFeed({ onOpenTag }: { onOpenTag?: (tag: string) => void }) {
  const qc = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const showAdult = useAuthStore((s) => s.user?.showAdult);
  // Кэш "per-story reveal" для SUGGESTIVE — сняли размытие на конкретной сторис.
  const [softRevealed, setSoftRevealed] = useState<Set<string>>(new Set());

  const confirmAdult = async () => {
    try {
      await apiClient.post('/api/users/adult-consent', { show: true });
      useAuthStore.setState((s) => ({
        user: s.user ? { ...s.user, showAdult: true, ageVerifiedAt: new Date().toISOString() } : s.user,
      }));
    } catch { /* ignore */ }
  };
  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['stories', 'for-you'],
    queryFn: ({ pageParam }) => storiesApi.getForYou(pageParam as string | undefined, 10),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = useMemo<ForYouItem[]>(
    () => (data?.pages ?? []).flatMap((p) => p.items),
    [data],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const viewedRef = useRef<Set<string>>(new Set());
  const [soundTrackId, setSoundTrackId] = useState<string | null>(null);
  const [duetFor, setDuetFor] = useState<{ parentId: string; mode: 'DUET' | 'STITCH' } | null>(null);
  // Android-паритет: эмодзи-стрип (id истории с открытым стрипом) + комменты-шит
  const [reactionsFor, setReactionsFor] = useState<string | null>(null);
  const [commentsFor, setCommentsFor] = useState<ForYouItem | null>(null);

  // Observe which card is centered → record view + preload next page.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio >= 0.6) {
            const id = (entry.target as HTMLElement).dataset.storyId!;
            setActiveId(id);
            if (!viewedRef.current.has(id)) {
              viewedRef.current.add(id);
              storiesApi.markViewed(id).catch(() => {/* silent */});
            }
          }
        }
      },
      { root, threshold: [0.6] },
    );
    root.querySelectorAll<HTMLElement>('[data-story-id]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [items.length]);

  // Navigation helper — прокручивает контейнер ровно на одну карточку вверх/вниз.
  // Каждая сторис занимает всю высоту (h-full + snap-mandatory), поэтому
  // делаем scroll by clientHeight; smooth behavior триггерит snap анимацию.
  const scrollByPage = (direction: 1 | -1) => {
    const root = containerRef.current;
    if (!root) return;
    root.scrollBy({ top: direction * root.clientHeight, behavior: 'smooth' });
  };

  // Keyboard — ↑/↓/PageUp/PageDown/Space/Home/End для навигации без мышки.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Игнорируем если пользователь печатает в input/textarea.
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault(); scrollByPage(1);
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault(); scrollByPage(-1);
      } else if (e.key === ' ') {
        e.preventDefault(); scrollByPage(1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Infinite scroll trigger
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const sentinel = root.querySelector<HTMLElement>('[data-sentinel]');
    if (!sentinel) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage) fetchNextPage();
      },
      { root, threshold: 0.1 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasNextPage, fetchNextPage, items.length]);

  const likeMut = useMutation({
    mutationFn: (id: string) => storiesApi.toggleLike(id),
    onSuccess: (res, id) => {
      qc.setQueryData<{ pages: { items: ForYouItem[] }[] } | undefined>(
        ['stories', 'for-you'],
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            pages: prev.pages.map((p) => ({
              ...p,
              items: p.items.map((it) =>
                it.id === id ? { ...it, isLiked: res.liked, likeCount: res.likeCount } : it,
              ),
            })),
          };
        },
      );
    },
  });

  const shareMut = useMutation({
    mutationFn: (id: string) => storiesApi.recordShare(id),
  });

  const reactMut = useMutation({
    mutationFn: ({ id, emoji }: { id: string; emoji: string }) => storiesApi.toggleReaction(id, emoji),
  });

  // Локальный бамп счётчика комментариев после отправки (без рефетча всей ленты)
  const bumpCommentCount = (id: string) => {
    qc.setQueryData<{ pages: { items: ForYouItem[] }[] } | undefined>(
      ['stories', 'for-you'],
      (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: prev.pages.map((p) => ({
            ...p,
            items: p.items.map((it) => (it.id === id ? { ...it, commentCount: it.commentCount + 1 } : it)),
          })),
        };
      },
    );
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-full text-surface-400">Загрузка…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-surface-400 px-6 text-center">
        Пока нет рекомендаций — поставьте лайки нескольким sторисам, чтобы лента настроилась.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-full w-full overflow-y-auto snap-y snap-mandatory scrollbar-none bg-black"
    >
      {items.map((it) => {
        const isAdult = it.contentRating === 'ADULT';
        const isSuggestive = it.contentRating === 'SUGGESTIVE';
        const needsAgeGate = isAdult && !showAdult;
        const softBlur = isSuggestive && !softRevealed.has(it.id);
        const mediaBlurClass = needsAgeGate || softBlur ? 'blur-2xl scale-110' : '';
        return (
        <div
          key={it.id}
          data-story-id={it.id}
          className="h-full w-full snap-start relative flex items-center justify-center"
        >
          {it.mimeType.startsWith('video/') ? (
            <video
              src={it.mediaUrl}
              poster={it.thumbnailUrl}
              className={clsx('max-h-full max-w-full object-contain transition-[filter] duration-200', mediaBlurClass)}
              autoPlay={activeId === it.id && !needsAgeGate}
              loop
              playsInline
              muted={activeId !== it.id}
              controls={false}
            />
          ) : (
            <img
              src={it.mediaUrl}
              alt=""
              className={clsx('max-h-full max-w-full object-contain transition-[filter] duration-200', mediaBlurClass)}
            />
          )}

          {/* Hard 18+ gate для ADULT */}
          {needsAgeGate && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 p-6 text-center">
              <div className="max-w-xs space-y-4 text-white">
                <div className="w-16 h-16 mx-auto rounded-full bg-red-600/20 flex items-center justify-center">
                  <AlertTriangle size={32} className="text-red-400" />
                </div>
                <h3 className="text-lg font-bold">Контент 18+</h3>
                <p className="text-sm text-surface-300">
                  В этой сторис деликатное содержимое. Подтверди что тебе 18 и более.
                </p>
                <button
                  onClick={confirmAdult}
                  className="w-full py-2 bg-red-600 hover:bg-red-500 rounded-lg font-medium"
                >
                  Мне 18+ · показать
                </button>
                <p className="text-[10px] text-surface-500">
                  Согласие запомнится для всех ADULT-сторис в этой сессии.
                </p>
              </div>
            </div>
          )}

          {/* Soft blur для SUGGESTIVE — tap to reveal */}
          {softBlur && !needsAgeGate && (
            <button
              onClick={() => setSoftRevealed((s) => new Set(s).add(it.id))}
              className="absolute inset-0 z-10 flex items-center justify-center"
            >
              <div className="bg-black/40 backdrop-blur-sm text-white px-5 py-3 rounded-full flex items-center gap-2">
                <Eye size={16} /> Показать
              </div>
            </button>
          )}

          {/* ── Верхняя карточка автора (Android ForYouScreen: top, black-плашка,
                 аватар 32dp + имя 13sp + @username 10sp + подписка #FF3B5C) ── */}
          <div
            className="absolute left-3 right-3 z-10 flex items-center gap-2 bg-black/45 rounded-2xl px-2 py-1.5"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 56px)' }}
          >
            <Avatar src={it.author.avatarUrl} username={it.author.displayName} size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-white text-[13px] font-semibold leading-tight truncate">{it.author.displayName}</p>
              <p className="text-[10px] leading-tight truncate" style={{ color: '#CBD5E1' }}>@{it.author.username ?? it.author.displayName}</p>
            </div>
            {currentUserId && it.author.id !== currentUserId && (
              <FollowButton userId={it.author.id} className="flex-shrink-0" />
            )}
          </div>

          {/* ── Правый столбец действий (Android: чистые белые иконки + label,
                 spacing 14dp, лайк-актив #FF3B30, эмодзи-стрип) ── */}
          <div
            className="absolute right-2 z-10 flex flex-col items-center text-white"
            style={{ bottom: 170, gap: 14, filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))' }}
          >
            <button onClick={() => likeMut.mutate(it.id)} className="flex flex-col items-center gap-0.5">
              <Heart size={30} fill={it.isLiked ? '#FF3B30' : 'none'} style={it.isLiked ? { color: '#FF3B30' } : undefined} />
              <span className="text-[13px] font-semibold">{it.likeCount}</span>
            </button>
            <button onClick={() => setCommentsFor(it)} className="flex flex-col items-center gap-0.5">
              <MessageCircle size={30} />
              <span className="text-[13px] font-semibold">{it.commentCount}</span>
            </button>
            <button onClick={() => shareMut.mutate(it.id)} className="flex flex-col items-center gap-0.5">
              <Share2 size={30} />
              <span className="text-[13px] font-semibold">{it.shareCount}</span>
            </button>
            {/* Эмодзи-реакции: стрип ❤️🔥👏😂😮 на black/72 r24 (Android) */}
            <div className="relative">
              <button onClick={() => setReactionsFor(reactionsFor === it.id ? null : it.id)} className="flex flex-col items-center">
                <Smile size={30} />
              </button>
              {reactionsFor === it.id && (
                <>
                  <div className="fixed inset-0 z-0" onClick={() => setReactionsFor(null)} />
                  <div className="absolute right-12 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1 bg-black/70 rounded-3xl px-2 py-1">
                    {['❤️', '🔥', '👏', '😂', '😮'].map((e) => (
                      <button
                        key={e}
                        className="text-[22px] px-1"
                        onClick={() => { reactMut.mutate({ id: it.id, emoji: e }); setReactionsFor(null); }}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setDuetFor({ parentId: it.id, mode: 'DUET' })}
              className="flex flex-col items-center gap-0.5"
              title="Снять ответ (Duet)"
            >
              <Clapperboard size={30} />
              <span className="text-[13px] font-semibold">Duet</span>
            </button>
          </div>

          {/* ── Нижняя плашка (Android: bg black/42, caption + «Комментариев: N»
                 + инлайн-поле «Добавить комментарий…» r20 + музыка) ── */}
          <div
            className="absolute inset-x-0 bottom-0 z-10 text-white bg-black/40 px-3 pt-2"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
          >
            {it.caption && (
              <div className="text-sm mb-1 whitespace-pre-wrap pr-14">
                {renderCaption(it.caption, onOpenTag)}
              </div>
            )}
            <button onClick={() => setCommentsFor(it)} className="block text-xs mb-1.5" style={{ color: '#CBD5E1' }}>
              {it.commentCount > 0 ? `Комментариев: ${it.commentCount} · посмотреть все →` : 'Комментариев пока нет'}
            </button>
            <InlineCommentInput storyId={it.id} onSent={() => bumpCommentCount(it.id)} />
            {it.musicTrack && (
              <button
                onClick={() => setSoundTrackId(it.musicTrack!.id)}
                className="flex items-center gap-1.5 text-xs text-white mt-1.5 max-w-full"
              >
                <Music size={14} />
                <span className="truncate">
                  {it.musicTrack.artist} — {it.musicTrack.title}
                </span>
              </button>
            )}
          </div>
        </div>
        );
      })}
      <div data-sentinel className="h-8" />

      {/* Nav arrows — видны только на desktop (mobile использует свайпы) */}
      <button
        onClick={() => scrollByPage(-1)}
        className="hidden md:flex fixed right-4 bottom-24 z-30 w-12 h-12 rounded-full bg-surface-900/70 backdrop-blur text-white items-center justify-center hover:bg-surface-800 transition"
        title="Предыдущая (↑)"
      >
        <ChevronUp size={24} />
      </button>
      <button
        onClick={() => scrollByPage(1)}
        className="hidden md:flex fixed right-4 bottom-8 z-30 w-12 h-12 rounded-full bg-surface-900/70 backdrop-blur text-white items-center justify-center hover:bg-surface-800 transition"
        title="Следующая (↓)"
      >
        <ChevronDown size={24} />
      </button>

      {soundTrackId && (
        <SoundPage trackId={soundTrackId} onClose={() => setSoundTrackId(null)} />
      )}

      {commentsFor && (
        <CommentsSheet
          story={commentsFor}
          onClose={() => setCommentsFor(null)}
          onSent={() => bumpCommentCount(commentsFor.id)}
        />
      )}

      {duetFor && (
        <StoryCreateModal
          parentStoryId={duetFor.parentId}
          replyMode={duetFor.mode}
          onClose={() => setDuetFor(null)}
          onCreated={() => {
            setDuetFor(null);
            qc.invalidateQueries({ queryKey: ['stories', 'for-you'] });
          }}
        />
      )}
    </div>
  );
}

/**
 * Инлайн-поле «Добавить комментарий…» в нижней плашке — как в Android
 * ForYouScreen (OutlinedTextField r20, бордер #303030 → #7c3aed при фокусе,
 * send-иконка #7c3aed при непустом вводе).
 */
function InlineCommentInput({ storyId, onSent }: { storyId: string; onSent: () => void }) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await storiesApi.addComment(storyId, text);
      setDraft('');
      onSent();
    } catch { /* silent — поле остаётся с текстом */ }
    setSending(false);
  };

  return (
    <div className="flex items-center gap-1">
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, 500))}
        onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
        placeholder="Добавить комментарий…"
        className="flex-1 bg-transparent text-white text-sm rounded-[20px] px-3.5 py-2 outline-none border transition-colors placeholder:text-[#707070]"
        style={{ borderColor: draft ? '#7c3aed' : '#303030', minHeight: 40 }}
      />
      <button
        onClick={send}
        disabled={!draft.trim() || sending}
        className="w-10 h-10 flex items-center justify-center flex-shrink-0"
        aria-label="Отправить комментарий"
      >
        <SwipaSend size={18} style={{ color: draft.trim() ? '#7c3aed' : '#505050' }} />
      </button>
    </div>
  );
}

/** Bottom-sheet со списком комментариев истории (открывается с «посмотреть все»). */
function CommentsSheet({ story, onClose, onSent }: { story: ForYouItem; onClose: () => void; onSent: () => void }) {
  const { data: comments, refetch } = useQuery({
    queryKey: ['story-comments', story.id],
    queryFn: () => storiesApi.getComments(story.id),
  });

  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-surface-900 rounded-t-2xl max-h-[70%] flex flex-col"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-800">
          <span className="text-surface-50 text-sm font-semibold">Комментарии ({story.commentCount})</span>
          <button onClick={onClose} className="text-surface-400 p-1" aria-label="Закрыть"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {(comments ?? []).length === 0 && (
            <p className="text-surface-400 text-sm text-center py-6">Комментариев пока нет</p>
          )}
          {(comments ?? []).map((c) => (
            <div key={c.id} className="py-2">
              <p className="text-xs font-semibold" style={{ color: '#E5E7EB' }}>{c.author.displayName}</p>
              <p className="text-sm text-surface-200 whitespace-pre-wrap">{c.content}</p>
            </div>
          ))}
        </div>
        <div className="px-3 py-2 border-t border-surface-800">
          <InlineCommentInput storyId={story.id} onSent={() => { onSent(); refetch(); }} />
        </div>
      </div>
    </div>
  );
}

function renderCaption(caption: string, onOpenTag?: (tag: string) => void) {
  // Linkify hashtags — `#foo` is rendered as a clickable span.
  const parts = caption.split(/(\s+)/);
  return parts.map((chunk, i) => {
    const m = chunk.match(/^#([\p{L}\p{N}_]{1,64})$/u);
    if (m) {
      const tag = m[1].toLocaleLowerCase('ru-RU');
      return (
        <button
          key={i}
          className="text-brand-400 hover:underline"
          onClick={() => onOpenTag?.(tag)}
        >
          {chunk}
        </button>
      );
    }
    return <span key={i}>{chunk}</span>;
  });
}
